import { Component, OnInit, OnDestroy, Input, Output } from '@angular/core';
import { Subscription, combineLatest } from 'rxjs';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';

import { Team } from 'src/app/shared/models/team/team';
import { TeamWrite } from 'src/app/shared/models/team/teamWrite';
import { TeamClone } from 'src/app/shared/models/team/teamClone';
import { LineupErrorStateMatcher } from 'src/app/shared/services/error-state-matcher.service';
import { ActivatedRoute } from '@angular/router';
import { TeamService } from 'src/app/shared/services/team.service';
import { AttributeService } from 'src/app/shared/services/attribute.service';
import {
  DateLessThanValidator,
  MustHaveStartDateIfEndDateValidator
} from 'src/app/shared/validators/date.validator';
import { UniqueValidator } from 'src/app/shared/validators/unique.validator';
import { NotEmptyValidator } from 'src/app/shared/validators/not-empty.validator';
import { AttributeEntityType } from 'src/app/shared/models/attributes/attributeEntityType';
import { publishLast } from 'rxjs/operators';
import { AttributesEditorDataSource } from 'src/shared-app-portal/components/attributes-editor/attributesEditorDataSource';

@Component({
  selector: 'app-team-edit-details',
  templateUrl: 'team-edit-details.component.html',
  styleUrls: ['team-edit-details.component.scss'],
  standalone: false
})
export class TeamEditDetailsComponent implements OnInit, OnDestroy {
  private subscriptions: Array<Subscription> = new Array<Subscription>();
  private teamNames: Array<string> = new Array<string>();

  public saving: boolean = false;
  public teamForm: UntypedFormGroup;
  public dateFormGroup: UntypedFormGroup;
  public errorMessage: any;
  public team: Team;
  public teamWrite: TeamWrite;
  public matcher = new LineupErrorStateMatcher();
  public attributesEditorDataSource = new AttributesEditorDataSource();

  @Input()
  public cloneMode: boolean = false;
  @Input()
  public teamId: string;

  public get formTitle(): string {
    return this.team?.name;
  }

  @Input()
  public get valid(): boolean {
    return (
      this.teamForm?.valid && this.attributesEditorDataSource?.isValid && this.dateFormGroup?.valid
    );
  }

  @Input()
  public get outputTeam(): TeamWrite {
    if (this.teamWrite) {
      this.assignFormValuesToTeam();
    }
    return this.teamWrite;
  }

  @Input()
  public get outputTeamClone(): TeamClone {
    if (this.teamWrite) {
      this.assignFormValuesToTeam();
    }
    return TeamClone.fromTeamWrite(this.teamWrite, this.teamForm.value.copyPeople);
  }

  constructor(
    private attributeService: AttributeService,
    private teamService: TeamService,
    private route: ActivatedRoute,
    private formBuilder: UntypedFormBuilder
  ) {}

  ngOnInit(): void {
    this.buildTeamForm([]);
    this.getTeamFormData();

    if (!this.cloneMode) {
      this.route.parent.params.subscribe((params) => {
        this.teamService.teams.pipe(publishLast());
      });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  getTeamFormData() {
    let comp = this;
    let combined = this.cloneMode
      ? combineLatest(
          this.attributeService.attributes,
          this.teamService.teams,
          this.teamService.getTeamById(this.teamId)
        )
      : combineLatest(
          this.attributeService.attributes,
          this.teamService.teams,
          this.teamService.team
        );

    this.subscriptions.push(
      combined.subscribe(
        (data) => {
          let attrs = data[0];
          let teams = data[1];
          let team = data[2];

          this.team = team;
          this.teamWrite = this.team.toTeamWrite(this.cloneMode);

          this.buildTeamForm(this.getUniqueTeamNames(teams, this.team));

          let attributeDefinitions = attrs.filter((f) => f.entityType == AttributeEntityType.Team);
          comp.attributesEditorDataSource.load(comp.team.attributes, attributeDefinitions);
        },
        (error) => (this.errorMessage = <any>error)
      )
    );
  }

  private assignFormValuesToTeam(): void {
    const teamFormModel = this.teamForm.value;
    const dateFormModel = this.dateFormGroup.value;

    this.teamWrite.name = teamFormModel.name as string;
    this.teamWrite.publicName = teamFormModel.publicName
      ? (teamFormModel.publicName as string)
      : null;
    this.teamWrite.startDate = dateFormModel.startDate ? new Date(dateFormModel.startDate) : null;
    this.teamWrite.endDate = dateFormModel.endDate ? new Date(dateFormModel.endDate) : null;
    this.teamWrite.attributes = this.attributesEditorDataSource.hasAttributes
      ? this.attributesEditorDataSource.asAttributeWrites()
      : [];
  }

  private buildTeamForm(teamNames: Array<string>) {
    this.dateFormGroup = this.formBuilder.group({
      startDate: [this.teamWrite ? this.teamWrite.startDate : null],
      endDate: [this.teamWrite ? this.teamWrite.endDate : null]
    });

    this.dateFormGroup.setValidators([
      DateLessThanValidator('startDate', 'endDate'),
      MustHaveStartDateIfEndDateValidator('startDate', 'endDate')
    ]);

    this.teamForm = this.formBuilder.group({
      name: [
        this.teamWrite ? this.teamWrite.name : '',
        [
          Validators.minLength(4),
          Validators.maxLength(255),
          UniqueValidator(teamNames, true, 'Team name must be unique'),
          NotEmptyValidator('Team name is required')
        ]
      ],
      publicName: [
        this.teamWrite && this.teamWrite.publicName ? this.teamWrite.publicName : '',
        [Validators.minLength(4), Validators.maxLength(255)]
      ],
      dateGroup: this.dateFormGroup,
      copyPeople: false
    });
  }

  private getUniqueTeamNames(teams: Array<Team>, team: Team) {
    let teamNames = teams.map((t) => t.name);
    if (!this.cloneMode) {
      teamNames = teamNames.filter((t) => t != team?.name);
    }
    return teamNames;
  }
}
